import React, { useMemo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, AlertCircle, Filter } from "lucide-react";
import { useTelemetryData } from "@/hooks/useTelemetry";
import { useLaps } from "@/hooks/useLaps";
import type { Session } from "@/types";

interface AccelerationScatterPlotProps {
  session: Session;
 className?: string;
}

interface ScatterPoint {
  x: number; // lateral acceleration
  y: number; // longitudinal acceleration
}

export const AccelerationScatterPlot: React.FC<
  AccelerationScatterPlotProps
> = ({ session, className = "" }) => {
  const { t } = useTranslation(["pages", "common"]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showValidLapsOnly, setShowValidLapsOnly] = useState(true);

  // Fetch lap data to filter by valid laps
  const { data: laps = [] } = useLaps(session.id);

  // Note: useTelemetryLaps could be used for more accurate lap segmentation in the future
  // const { data: telemetryLaps } = useTelemetryLaps(session.id);

  // Fetch telemetry data for lateral and longitudinal acceleration
  const {
    data: telemetryData,
    isLoading,
    error,
  } = useTelemetryData(
    session.id,
    {
      channels: ["LateralAcc", "InlineAcc"],
      maxPoints: 25000, // Increased limit for better data coverage
    },
    true,
  );

  // Calculate valid lap time ranges for filtering
  const validLapTimeRanges = useMemo(() => {
    if (!showValidLapsOnly || laps.length === 0) return null;

    const validLaps = laps.filter((lap) => lap.isValid);
    if (validLaps.length === 0) return [];

    // Calculate cumulative time ranges for valid laps
    // This is an approximation - in real implementation, we'd need lap start times from telemetry
    const samplingInfo = telemetryData?.result?.samplingInfo;
    if (!samplingInfo?.startTime || !samplingInfo?.endTime) return [];

    const totalSessionTime = samplingInfo.endTime - samplingInfo.startTime;
    const totalLaps = laps.length;
    const avgLapTime = totalSessionTime / totalLaps;

    const ranges = validLaps.map((lap) => {
      const lapIndex = laps.findIndex((l) => l.id === lap.id);
      const startTime = samplingInfo.startTime + lapIndex * avgLapTime;
      const endTime = startTime + avgLapTime;
      return { startTime, endTime, lapNumber: lap.lapNumber };
    });

    // Note: This is an approximation based on estimated lap timing
    // In a production system, lap segments would be provided by the telemetry API

    return ranges;
  }, [laps, showValidLapsOnly, telemetryData]);

  // Process telemetry data into scatter points
  const scatterPoints = useMemo((): ScatterPoint[] => {
    if (!telemetryData?.result?.data) return [];

    let filteredData = telemetryData.result.data;

    // Filter by valid lap time ranges if enabled
    if (showValidLapsOnly && laps.length > 0) {
      const validLaps = laps.filter((lap) => lap.isValid);
      const validLapRatio = validLaps.length / laps.length;

      if (validLapRatio < 1.0) {
        // If we have invalid laps, filter data using time ranges
        if (validLapTimeRanges && validLapTimeRanges.length > 0) {
          filteredData = telemetryData.result.data.filter((point) => {
            return validLapTimeRanges.some(
              (range) =>
                point.timestamp >= range.startTime &&
                point.timestamp <= range.endTime,
            );
          });

          // If filtering resulted in no data (time range estimation failed),
          // fall back to sampling based on valid lap ratio
          if (filteredData.length === 0) {
            const sampleSize = Math.floor(
              telemetryData.result.data.length * validLapRatio,
            );
            if (sampleSize > 0) {
              // Take evenly distributed samples across the session
              const stepSize = Math.floor(
                telemetryData.result.data.length / sampleSize,
              );
              filteredData = telemetryData.result.data
                .filter((_, index) => index % stepSize === 0)
                .slice(0, sampleSize);
            }
          }
        } else {
          // Force sampling even without time ranges
          const sampleSize = Math.floor(
            telemetryData.result.data.length * validLapRatio,
          );
          if (sampleSize > 0) {
            const stepSize = Math.floor(
              telemetryData.result.data.length / sampleSize,
            );
            filteredData = telemetryData.result.data
              .filter((_, index) => index % stepSize === 0)
              .slice(0, sampleSize);
          }
        }
      }
      // If all laps are valid, show all data (no filtering needed)
    }

    return filteredData
      .map((point) => ({
        x: point.values["LateralAcc"] || 0,
        y: point.values["InlineAcc"] || 0,
      }))
      .filter(
        (point) =>
          // Filter out invalid data points
          !isNaN(point.x) &&
          !isNaN(point.y) &&
          Math.abs(point.x) < 10 &&
          Math.abs(point.y) < 10, // Reasonable acceleration limits
      );
  }, [telemetryData, showValidLapsOnly, validLapTimeRanges, laps]);

  // Calculate percentile bounds for ellipse drawing
  const percentileBounds = useMemo(() => {
    if (scatterPoints.length === 0) return null;

    const xValues = scatterPoints.map((p) => p.x).sort((a, b) => a - b);
    const yValues = scatterPoints.map((p) => p.y).sort((a, b) => a - b);

    const getPercentile = (sortedArray: number[], percentile: number) => {
      const index = Math.floor((sortedArray.length * percentile) / 100);
      return sortedArray[Math.min(index, sortedArray.length - 1)];
    };

    return {
      leftBound: getPercentile(xValues, 1), // 1% lateral (negative)
      rightBound: getPercentile(xValues, 99), // 99% lateral (positive)
      bottomBound: getPercentile(yValues, 1), // 1% longitudinal (negative)
      topBound: getPercentile(yValues, 99), // 99% longitudinal (positive)
    };
  }, [scatterPoints]);

  // Calculate bounds for the plot - ensure 0,0 is in the center
  const bounds = useMemo(() => {
    if (scatterPoints.length === 0) {
      return { minX: -2, maxX: 2, minY: -2, maxY: 2 };
    }

    const xs = scatterPoints.map((p) => p.x);
    const ys = scatterPoints.map((p) => p.y);

    // Find the maximum absolute value to create symmetric bounds around 0
    const maxAbsX = Math.max(
      Math.abs(Math.min(...xs)),
      Math.abs(Math.max(...xs)),
      1,
    );
    const maxAbsY = Math.max(
      Math.abs(Math.min(...ys)),
      Math.abs(Math.max(...ys)),
      1,
    );

    return {
      minX: -maxAbsX,
      maxX: maxAbsX,
      minY: -maxAbsY,
      maxY: maxAbsY,
    };
  }, [scatterPoints]);

  // Canvas dimensions and scaling - memoized to avoid recreation
  const canvasConfig = useMemo(() => {
    const canvasWidth = 500;
    const canvasHeight = 500;
    const margin = 50;
    const plotWidth = canvasWidth - 2 * margin;
    const plotHeight = canvasHeight - 2 * margin;

    // Scale functions
    const scaleX = (x: number) =>
      margin + ((x - bounds.minX) / (bounds.maxX - bounds.minX)) * plotWidth;

    const scaleY = (y: number) =>
      canvasHeight -
      margin -
      ((y - bounds.minY) / (bounds.maxY - bounds.minY)) * plotHeight;

    return {
      canvasWidth,
      canvasHeight,
      margin,
      plotWidth,
      plotHeight,
      scaleX,
      scaleY,
    };
  }, [bounds]);

  const {
    canvasWidth,
    canvasHeight,
    margin,
    plotWidth,
    plotHeight,
    scaleX,
    scaleY,
  } = canvasConfig;

  // Draw the scatter plot on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || scatterPoints.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set up high DPI rendering
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw grid lines
    ctx.strokeStyle = "#f3f4f6";
    ctx.lineWidth = 1;
    ctx.setLineDash([]);

    // Vertical grid lines
    for (let i = 0; i <= 10; i++) {
      const x = margin + (i / 10) * plotWidth;
      ctx.beginPath();
      ctx.moveTo(x, margin);
      ctx.lineTo(x, canvasHeight - margin);
      ctx.stroke();
    }

    // Horizontal grid lines
    for (let i = 0; i <= 10; i++) {
      const y = margin + (i / 10) * plotHeight;
      ctx.beginPath();
      ctx.moveTo(margin, y);
      ctx.lineTo(canvasWidth - margin, y);
      ctx.stroke();
    }

    // Draw main center axes (0,0 crosshairs)
    ctx.strokeStyle = "#374151";
    ctx.lineWidth = 1;

    // Center vertical line (x = 0)
    const centerX = scaleX(0);
    ctx.beginPath();
    ctx.moveTo(centerX, margin);
    ctx.lineTo(centerX, canvasHeight - margin);
    ctx.stroke();

    // Center horizontal line (y = 0)
    const centerY = scaleY(0);
    ctx.beginPath();
    ctx.moveTo(margin, centerY);
    ctx.lineTo(canvasWidth - margin, centerY);
    ctx.stroke();

    // Draw scatter points
    ctx.fillStyle = "#3b82f6";
    ctx.globalAlpha = 0.6;

    // Use efficient batch rendering for points
    scatterPoints.forEach((point) => {
      const x = scaleX(point.x);
      const y = scaleY(point.y);

      ctx.beginPath();
      ctx.arc(x, y, 1.0, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw percentile ellipse (performance envelope)
    if (percentileBounds) {
      const centerX = scaleX(0);
      const centerY = scaleY(0);

      // Calculate ellipse dimensions based on percentile bounds
      const radiusX = Math.max(
        Math.abs(scaleX(percentileBounds.rightBound) - centerX),
        Math.abs(scaleX(percentileBounds.leftBound) - centerX),
      );
      const radiusY = Math.max(
        Math.abs(scaleY(percentileBounds.topBound) - centerY),
        Math.abs(scaleY(percentileBounds.bottomBound) - centerY),
      );

      ctx.strokeStyle = "#c1c5cb"; // Light gray
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]); // Dashed line
      ctx.globalAlpha = 0.7;

      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
      ctx.stroke();

      ctx.setLineDash([]); // Reset line dash
      ctx.globalAlpha = 1;
    }

    ctx.globalAlpha = 1;

    // Draw axes labels
    ctx.fillStyle = "#6b7280";
    ctx.font = "12px sans-serif";
    ctx.textAlign = "center";

    // X-axis label
    ctx.fillText(
      t("pages:sessions.telemetry.acceleration.lateralAxisLabel"),
      canvasWidth / 2,
      canvasHeight - 15,
    );

    // Y-axis label
    ctx.save();
    ctx.translate(15, canvasHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(t("pages:sessions.telemetry.acceleration.longitudinalAxisLabel"), 0, 0);
    ctx.restore();

    // Draw tick labels
    ctx.font = "10px sans-serif";

    // X-axis ticks - create evenly spaced ticks
    const xTickCount = 5;
    for (let i = 0; i <= xTickCount; i++) {
      const value =
        bounds.minX + (i / xTickCount) * (bounds.maxX - bounds.minX);
      const x = scaleX(value);
      ctx.textAlign = "center";

      // Highlight center (0) tick
      ctx.fillStyle = value === 0 ? "#374151" : "#9ca3af";
      ctx.font = value === 0 ? "bold 10px sans-serif" : "10px sans-serif";
      ctx.fillText(value.toFixed(1), x, canvasHeight - 30);
    }

    // Y-axis ticks - create evenly spaced ticks
    const yTickCount = 5;
    for (let i = 0; i <= yTickCount; i++) {
      const value =
        bounds.minY + (i / yTickCount) * (bounds.maxY - bounds.minY);
      const y = scaleY(value);
      ctx.textAlign = "center";

      // Highlight center (0) tick
      ctx.fillStyle = value === 0 ? "#374151" : "#9ca3af";
      ctx.font = value === 0 ? "bold 10px sans-serif" : "10px sans-serif";
      ctx.fillText(value.toFixed(1), 30, y + 4);
    }
  }, [
    scatterPoints,
    bounds,
    canvasWidth,
    canvasHeight,
    margin,
    plotWidth,
    plotHeight,
    scaleX,
    scaleY,
    percentileBounds,
    t,
  ]);

  if (isLoading) {
    return (
      <div
 className={`bg-white rounded-lg border border-secondary-200 p-6 ${className}`}
      >
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div className="h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto mb-4 animate-spin" />
            <p className="text-secondary-600">{t("pages:sessions.telemetry.acceleration.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !telemetryData?.result?.data) {
    return (
      <div
 className={`bg-white rounded-lg border border-secondary-200 p-6 ${className}`}
      >
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <AlertCircle className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-secondary-600 mb-2">
              {t("pages:sessions.telemetry.acceleration.noData")}
            </p>
            <p className="text-secondary-500">
              {t("pages:sessions.telemetry.acceleration.notFound")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
 <div className={`bg-white rounded-lg border border-secondary-200 ${className}`}>
      {/* Header */}
 <div className="border-b border-secondary-200 p-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-3">
 <Activity className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-secondary-900">
              {t("pages:sessions.telemetry.acceleration.title")}
            </h3>
            <div className="text-sm text-secondary-600">
              {scatterPoints.length.toLocaleString()}{t("pages:sessions.telemetry.acceleration.pointsSuffix", { count: scatterPoints.length })}
            </div>
          </div>

          {/* Valid laps filter toggle */}
          {laps.length > 0 && (
 <div className="flex items-center space-x-2">
 <Filter className="h-4 w-4 text-secondary-400" />
 <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={showValidLapsOnly}
                  onChange={(e) => setShowValidLapsOnly(e.target.checked)}
 className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-secondary-700">
                  {t("pages:sessions.telemetry.acceleration.validLapsOnly", { count: laps.filter((lap) => lap.isValid).length })}
                </span>
              </label>
            </div>
          )}
        </div>
        <p className="text-sm text-secondary-500 mt-1">
          {t("pages:sessions.telemetry.acceleration.axisDescription")}
          {showValidLapsOnly &&
            laps.length > 0 &&
            (() => {
              const validLaps = laps.filter((lap) => lap.isValid);
              const validLapRatio = validLaps.length / laps.length;
              if (validLapRatio === 1.0) {
                return ` • ${t("pages:sessions.telemetry.acceleration.allLapsValid", { count: laps.length })}`;
              } else {
                return ` • ${t("pages:sessions.telemetry.acceleration.filteredToValid", { valid: validLaps.length, percent: Math.round(validLapRatio * 100) })}`;
              }
            })()}
        </p>
      </div>

      {/* Plot */}
 <div className="p-6">
 <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
 className="border border-secondary-300 rounded"
            style={{ width: canvasWidth, height: canvasHeight }}
          />
        </div>

        {/* Legend */}
 <div className="mt-4 text-center">
 <div className="inline-flex flex-wrap items-center justify-center space-x-4 text-sm text-secondary-600">
 <div className="flex items-center space-x-2">
 <div className="w-3 h-3 rounded-full bg-primary-500 opacity-60"></div>
              <span>{t("pages:sessions.telemetry.acceleration.dataPointsLegend")}</span>
            </div>
            <div className="text-secondary-400">•</div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-1 border-2 border-secondary-400 border-dashed"></div>
              <span>{t("pages:sessions.telemetry.acceleration.envelopeLegend")}</span>
            </div>
            <div className="text-secondary-400">•</div>
            <div>{t("pages:sessions.telemetry.acceleration.centerLinesLegend")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
